/* Winess custom JS — global enhancements applied across the theme.
 * Loaded from layout/theme.liquid with defer.
 *
 * Conventions:
 *   - Each feature isolated in an IIFE so a thrown error doesn't break siblings.
 *   - Guard with feature-presence checks; never assume markup exists.
 *   - Read DOM lazily — initialisers run on DOMContentLoaded.
 */

(() => {
  /* PDP main gallery — wire the overlay prev/next arrows to media-gallery.
   *
   * The buttons live inside the main <slider-component id="GalleryViewer-…">,
   * which Craft's SliderComponent normally drives by horizontal-scrolling the
   * media <ul>. In the "thumbnail" / "thumbnail_slider" desktop layouts the
   * media list isn't a real scroller (only one slide is visible at a time and
   * the rest have clientWidth: 0), so SliderComponent.initPages bails out and
   * the buttons end up dead. Switching the active media is media-gallery.js's
   * job — it exposes setActiveMedia(mediaId) and ties to thumbnails via
   * data-target. We intercept clicks on the main prev/next buttons and step
   * through the thumbnail list, which is the same path a thumbnail click
   * takes. */
  const initGalleryArrows = () => {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      const viewer = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
      const thumbsContainer = gallery.querySelector('slider-component[id^="GalleryThumbnails-"]');
      if (!viewer || !thumbsContainer) return;

      const prev = viewer.querySelector('.slider-button--prev');
      const next = viewer.querySelector('.slider-button--next');
      const counterCurrent = viewer.querySelector('.slider-counter--current');
      const counterTotal = viewer.querySelector('.slider-counter--total');
      if (!prev || !next) return;

      const getThumbs = () =>
        Array.from(thumbsContainer.querySelectorAll('.thumbnail-list__item[data-target]'));

      const getCurrentIndex = () => {
        const thumbs = getThumbs();
        const active = thumbs.findIndex((t) => t.querySelector('button[aria-current="true"]'));
        return active === -1 ? 0 : active;
      };

      const step = (delta) => (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const thumbs = getThumbs();
        if (thumbs.length === 0) return;
        const target = Math.min(thumbs.length - 1, Math.max(0, getCurrentIndex() + delta));
        const btn = thumbs[target].querySelector('button');
        if (btn) btn.click();
        updateDisabled();
        updateCounter();
      };

      const updateDisabled = () => {
        const thumbs = getThumbs();
        const i = getCurrentIndex();
        prev.toggleAttribute('disabled', i <= 0);
        next.toggleAttribute('disabled', i >= thumbs.length - 1);
      };

      const updateCounter = () => {
        const thumbs = getThumbs();
        if (counterCurrent) counterCurrent.textContent = String(getCurrentIndex() + 1);
        if (counterTotal) counterTotal.textContent = String(thumbs.length);
      };

      // Use capture so we run before SliderComponent's bound handler, then stop
      // propagation — SliderComponent would otherwise scroll a non-scrollable
      // list and leave the disabled state wrong.
      prev.addEventListener('click', step(-1), true);
      next.addEventListener('click', step(1), true);

      // Sync state on thumbnail clicks too (media-gallery handles the switch
      // but doesn't tell us; observe aria-current changes on thumbnails).
      const observer = new MutationObserver(() => {
        updateDisabled();
        updateCounter();
      });
      thumbsContainer.querySelectorAll('.thumbnail').forEach((t) => {
        observer.observe(t, { attributes: true, attributeFilter: ['aria-current'] });
      });

      updateDisabled();
      updateCounter();
    });
  };

  /* Vin Garde display-stores modal — global open/close behaviour.
   *
   * Section markup lives in `sections/winess-display-stores.liquid` (rendered
   * once globally from `layout/theme.liquid`). Triggers:
   *
   *   - Any `<a>` whose href ends with `#winess-display-modal`. Merchants set
   *     this on any URL field (e.g. a winess-guides "FIND OUT MORE" button)
   *     and the click is captured before the browser scrolls to the hash.
   *   - Any element carrying `[data-winess-display-modal-open]`. Used by the
   *     `snippets/winess-display-banner.liquid` button on tagged PDPs.
   *
   * Map iframes are kept in `data-src` until first open so the hidden modal
   * doesn't pull Google Maps tiles on every pageview. */
  const initDisplayStoresModal = () => {
    const modal = document.getElementById('winess-display-modal');
    if (!modal) return;

    let lastTrigger = null;
    let iframesHydrated = false;

    const hydrateIframes = () => {
      if (iframesHydrated) return;
      modal.querySelectorAll('iframe[data-src]').forEach((iframe) => {
        const src = iframe.getAttribute('data-src');
        if (src) iframe.setAttribute('src', src);
        iframe.removeAttribute('data-src');
      });
      iframesHydrated = true;
    };

    const openModal = (trigger) => {
      lastTrigger = trigger || document.activeElement;
      hydrateIframes();
      modal.hidden = false;
      document.body.classList.add('winess-display-modal-open');
      // Move focus into the dialog. Defer one frame so transitions can paint.
      requestAnimationFrame(() => {
        const focusTarget = modal.querySelector('.winess-display-modal__close') || modal;
        focusTarget.focus({ preventScroll: true });
      });
    };

    const closeModal = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove('winess-display-modal-open');
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus({ preventScroll: true });
      }
      lastTrigger = null;
    };

    // Open triggers (delegated; matches links added by section reloads in the editor).
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(
        'a[href$="#winess-display-modal"], [data-winess-display-modal-open]'
      );
      if (!trigger) return;
      // Don't intercept modifier-clicks — let cmd/ctrl+click work as usual on anchors.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      openModal(trigger);
    });

    // Close triggers — the X / anything tagged with the close attribute, plus
    // any click that lands outside the dialog box (backdrop or overlay padding).
    modal.addEventListener('click', (event) => {
      if (
        event.target.closest('[data-winess-display-modal-close]') ||
        !event.target.closest('.winess-display-modal__dialog')
      ) {
        event.preventDefault();
        closeModal();
      }
    });

    // Close on ESC. Bound at document level so it works regardless of focus.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        event.preventDefault();
        closeModal();
      }
    });

    // Simple focus trap — keeps Tab inside the dialog while open.
    modal.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || modal.hidden) return;
      const focusables = modal.querySelectorAll(
        'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  /* Lead conversion events + GCLID capture (enquiry-led business).
   *
   * Since 2026-07 ALL Google tracking flows through GTM (web GTM-TVWZ3FD,
   * loaded by the Stape app embed via client.winess.co.uk → server container
   * GTM-N8Q5SJ2D → GA4 + Google Ads). This block no longer loads gtag or fires
   * AW- conversions directly — it only pushes structured events into
   * window.dataLayer for GTM to pick up:
   *
   *   - generate_lead (lead_type 'quote_request') → quote form on a /products/ page
   *   - generate_lead (lead_type 'enquiry_form')  → native contact form
   *   - email_click                               → click on a[href^="mailto:"]
   *
   * GTM web tags "[Winess] GA4 - generate_lead / email_click" forward these to
   * GA4 via the server container, where "[Winess] GADS - QuoteRequest /
   * EnquiryForm / EmailClick" fire the Google Ads conversions. user_data on
   * generate_lead feeds Enhanced Conversions (keys must match the GTM
   * "dlv - user_data.*" variables). Phone-call tracking (GFN number swap) is
   * the GTM web tag "[Winess] GAds - Calls from Website (GFN)" — no theme code.
   *
   * Why this lives in theme JS and not a Customer Events pixel: the sandboxed
   * pixel can't see DOM clicks (mailto:) or our native /contact form posts —
   * and those ARE our conversions.
   *
   * GCLID capture runs on every page: stores the Google click id in a 90-day
   * cookie and injects a hidden contact[gclid] field into every contact form,
   * so it rides along in the enquiry email and the lead beacon. The Apps Script
   * logs it against the deal; won-quote revenue returns to Google Ads via the
   * Data Manager sheet (offline conversion import).
   *
   * Lead-intake endpoint (winess-automation.gs web app). On enquiry success the
   * full submission (incl. gclid) is beaconed there: it creates the tagged
   * customer in Shopify admin + logs to the Enquiries sheet. */
  const LEAD_ENDPOINT = 'https://script.google.com/macros/s/AKfycbyQgZNWhQ9aS0UzRVrBd_LtMzjP3IBNM9Q7NP1NhJ48mYXpQMdRW5gULoQmIHXg7Zpk/exec';
  const LEAD_SECRET = '26682610998b6c71ef135349a75e90b77770dc1b0f87d746';

  const initConversionTracking = () => {
    // All events go to the dataLayer; GTM owns every downstream tag.
    const pushEvent = (payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    };

    // GCLID capture → 90-day cookie + hidden field.
    const COOKIE = 'winess_gclid';
    const readCookie = (name) => {
      const m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : '';
    };
    const captureGclid = () => {
      const params = new URLSearchParams(window.location.search);
      const gclid = params.get('gclid') || params.get('gbraid') || params.get('wbraid');
      if (gclid) {
        const exp = new Date(Date.now() + 90 * 864e5).toUTCString();
        document.cookie = COOKIE + '=' + encodeURIComponent(gclid) + ';path=/;expires=' + exp + ';SameSite=Lax';
      }
      return readCookie(COOKIE);
    };
    const injectGclidField = (gclid) => {
      if (!gclid) return;
      document.querySelectorAll('form[action*="/contact"]').forEach((form) => {
        if (form.querySelector('input[name="contact[gclid]"]')) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'contact[gclid]';
        input.value = gclid;
        form.appendChild(input);
      });
    };

    // Stash the submission at submit time (bubble phase — runs AFTER the quote
    // modal's own handler has composed contact[body]), so the success page can
    // beacon it to the lead endpoint + attach enhanced-conversion user data.
    // Native POST means the page unloads; sessionStorage survives the reload.
    const stashLeadPayload = () => {
      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!/\/contact/.test(form.getAttribute('action') || '')) return;
        const formType = form.querySelector('input[name="form_type"]');
        if (formType && formType.value !== 'contact') return;
        try {
          const fd = new FormData(form);
          // Two different forms post to /contact:
          //  - the product quote modal: contact[first_name]/[last_name]/[phone]/
          //    [postcode]/[body] + product data-attrs
          //  - the native /pages/contact form: a single contact[Name], plus the
          //    TRANSLATED-label fields contact[Phone number] and contact[Comment]
          //    (Craft uses the label text as the field key). Read both so nothing
          //    is dropped on contact-page enquiries.
          let firstName = fd.get('contact[first_name]') || '';
          let lastName = fd.get('contact[last_name]') || '';
          if (!firstName && !lastName) {
            const parts = String(fd.get('contact[Name]') || '').trim().split(/\s+/).filter(Boolean);
            firstName = parts.shift() || '';
            lastName = parts.join(' ');
          }
          sessionStorage.setItem('winess_lead_payload', JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            email: fd.get('contact[email]') || '',
            phone: fd.get('contact[phone]') || fd.get('contact[Phone number]') || '',
            postcode: fd.get('contact[postcode]') || '',
            message: fd.get('contact[body]') || fd.get('contact[Comment]') || '',
            gclid: fd.get('contact[gclid]') || readCookie(COOKIE) || '',
            product: (form.querySelector('[data-winess-product-title]') || {}).value || '',
            variant: (form.querySelector('[data-winess-variant]') || {}).value || '',
            page: window.location.pathname,
          }));
        } catch (e) { /* fail-soft — native email path unaffected */ }
      });
    };

    const sendLeadBeacon = (lead) => {
      if (LEAD_ENDPOINT.indexOf('https://') !== 0) return; // placeholder ⇒ no-op
      const body = JSON.stringify(Object.assign({ secret: LEAD_SECRET }, lead));
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(LEAD_ENDPOINT, new Blob([body], { type: 'text/plain' }));
        } else {
          fetch(LEAD_ENDPOINT, { method: 'POST', body: body, mode: 'no-cors', keepalive: true });
        }
      } catch (e) { /* fail-soft */ }
    };

    // Enquiry success — both the native contact form and the quote modal reload
    // with ?contact_posted=true. Product page ⇒ quote request; else ⇒ enquiry.
    const trackEnquirySuccess = () => {
      // Two success signals: the raw ?contact_posted=true param, or the handoff
      // flag set by winess-request-quote.js (which strips the param for its own
      // toast before this script runs — see maybeRestoreSuccess there).
      const params = new URLSearchParams(window.location.search);
      let posted = params.get('contact_posted') === 'true';
      let path = window.location.pathname;
      if (!posted) {
        const handoff = sessionStorage.getItem('winess_contact_posted');
        if (handoff) {
          posted = true;
          path = handoff;
          sessionStorage.removeItem('winess_contact_posted');
        }
      }
      if (!posted) return;
      const guardKey = 'winess_conv_' + path; // no double-count on refresh
      if (sessionStorage.getItem(guardKey)) return;
      sessionStorage.setItem(guardKey, '1');

      // Consume the stashed submission: beacon it to the lead endpoint (Shopify
      // customer record + Enquiries sheet) and build the user_data payload for
      // Google Ads Enhanced Conversions (matched downstream by email/phone).
      let lead = null;
      try { lead = JSON.parse(sessionStorage.getItem('winess_lead_payload') || 'null'); } catch (e) { /* noop */ }
      const userData = {};
      if (lead) {
        try { sessionStorage.removeItem('winess_lead_payload'); } catch (e) { /* noop */ }
        sendLeadBeacon(lead);
        // Keys must match GTM's "dlv - user_data.*" variables.
        if (lead.email) userData.email_address = lead.email;
        if (lead.phone) userData.phone_number = lead.phone;
        if (lead.first_name) userData.first_name = lead.first_name;
        if (lead.last_name) userData.last_name = lead.last_name;
        if (lead.postcode) userData.postal_code = lead.postcode;
      }
      pushEvent({
        event: 'generate_lead',
        lead_type: /\/products\//.test(path) ? 'quote_request' : 'enquiry_form',
        user_data: userData,
      });
    };

    // Email taps only. Phone is tracked by the Google Forwarding Number via
    // the GTM "[Winess] GAds - Calls from Website (GFN)" tag (number swap +
    // real ≥60s call counted), so a tel:-tap event here would double-count.
    const trackClicks = () => {
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="mailto:"]');
        if (!link) return;
        pushEvent({ event: 'email_click' });
      });
    };

    const gclid = captureGclid();
    injectGclidField(gclid);
    stashLeadPayload();
    trackEnquirySuccess();
    trackClicks();
  };

  const init = () => {
    initGalleryArrows();
    initDisplayStoresModal();
    initConversionTracking();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
